import { DateTime } from "luxon"
import { EmploymentInformationValues } from ".."
import { CountryIsoCode, Employment } from "@oysterhr/schema"
import { WorkDayValuesLegacy } from "@oysterhr/feature-components"
import { getNextBusinessDay, isBusinessDay } from "@oysterhr/common"

export const getHoursPerDaySchedule = (daySchedule: WorkDayValuesLegacy) => {
  const from = DateTime.fromISO(daySchedule.from)
  const to = DateTime.fromISO(daySchedule.to)
  const currentDayDuration = to.diff(from)
  const hoursPerCurrentDay = currentDayDuration.as("hours")
  return hoursPerCurrentDay
}

export const getHoursPerWeekFromWorkSchedule = (
  schedule?: EmploymentInformationValues["workDays"] | null,
) => {
  const hoursPerWeek = Object.values(schedule || {})
    .filter(Boolean)
    .reduce<number | null>((hoursAcc, currentDaySchedule) => {
      if (!currentDaySchedule.from || !currentDaySchedule.to || hoursAcc === null) {
        return null
      }
      const hoursPerCurrentDay = getHoursPerDaySchedule(currentDaySchedule)
      // Truncate to 1 decimal
      return hoursAcc + Math.round(hoursPerCurrentDay * 10) / 10
    }, 0)
  return hoursPerWeek
}

export const getIsWorkScheduleSelected = (schedule: EmploymentInformationValues["workDays"]) =>
  Object.values(schedule || {}).filter(Boolean).length > 0

export const getIsHoursPerWeekAutoFilled = (
  schedule: EmploymentInformationValues["workDays"],
  employmentType: Employment["employmentType"],
) => getIsWorkScheduleSelected(schedule) && !!employmentType

export const hasHigherHiringSetupTime = (countryCode?: string | null) =>
  countryCode === CountryIsoCode.Us

export const USA_HIRING_SETUP_DAYS = 13
export const HIRING_SETUP_BUSINESS_DAYS = 5
/**
 * Helper function to help us with the edge case of a company admin creating a hiring flow on a Saturday / Sunday
 * In this specific days, we can't add 7 days to the currentDate, because that would mean that the next available business would be Monday, and it should be the following Friday
 * @param {Date} currentDate
 * @returns number
 */
const getNumberOfDaysToAdd = (currentDate: Date, countryCode?: string) => {
  if (hasHigherHiringSetupTime(countryCode)) {
    // US time to hire can take weeks, so we need to add 13 days (including weekends)
    return USA_HIRING_SETUP_DAYS
  }

  if (isBusinessDay(currentDate)) {
    // Includes next Saturday and Sunday
    return HIRING_SETUP_BUSINESS_DAYS + 2
  }

  // If it's Sunday we return 5; for Saturday we return 6.
  return currentDate.getDay() === 0 ? HIRING_SETUP_BUSINESS_DAYS : HIRING_SETUP_BUSINESS_DAYS + 1
}

/**
 * Depending on the current day of the month, this function should return the following:
 * Every calculation takes into account that we need to have a 5 business day buffer (current day + 5 business days)
 * If it's before the 15th, it should return the next business day
 * If it's after the 15th, and it doesn't overlap to next month, it should return the first business next of next month
 * If it overlaps to the next month, it should just return the next business day
 * @returns Date
 */
export const getMinimumEngagementStartDate = (countryCode?: string) => {
  const monthDayThreshold = 15
  // eslint-disable-next-line oyster/frog-forbidden-date
  const currentDate = new Date()

  // eslint-disable-next-line oyster/frog-forbidden-date
  const march25th2024 = new Date(2024, 2, 25)
  // eslint-disable-next-line oyster/frog-forbidden-date
  const april3rd2024 = new Date(2024, 3, 3)
  /**
   * ESD request: from March 25th until April 3rd
   * we should not allow a customer to select a start date prior to April 8th.
   */
  if (currentDate >= march25th2024 && currentDate <= april3rd2024) {
    // eslint-disable-next-line oyster/frog-forbidden-date
    return new Date(2024, 3, 8)
  }

  const currentDayOfMonth = currentDate.getDate()
  const numberOfDaysToAdd = getNumberOfDaysToAdd(currentDate, countryCode)
  // eslint-disable-next-line oyster/frog-forbidden-date
  const nextAvailableStartDate = new Date(currentDate)
  nextAvailableStartDate.setDate(currentDayOfMonth + numberOfDaysToAdd)

  // nextAvailableStartDate is before the 15th
  if (currentDayOfMonth <= monthDayThreshold) {
    return getNextBusinessDay(nextAvailableStartDate)
  } else {
    // eslint-disable-next-line oyster/frog-forbidden-date
    const lastDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

    // nextAvailableStartDate is in current month
    if (nextAvailableStartDate.getTime() <= lastDayOfCurrentMonth.getTime()) {
      const oneDay = 3600 * 1000 * 24
      // eslint-disable-next-line oyster/frog-forbidden-date
      const firstDayOfNextMonth = new Date(lastDayOfCurrentMonth.getTime() + oneDay)

      return getNextBusinessDay(firstDayOfNextMonth)
    } else {
      return getNextBusinessDay(nextAvailableStartDate)
    }
  }
}
